import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  LayoutDashboard,
  Settings,
  Users,
  Globe,
  LogOut,
  ChevronDown,
  HelpCircle,
  Menu,
  X,
  UserCheck,
  ArrowLeft
} from 'lucide-react';
import './AppLayout.css';

function AppLayout() {
  const { user, logout, selectedBlog, switchBlog, customerDocsOnly } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showBlogMenu, setShowBlogMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [workspaceMode, setWorkspaceMode] = useState(null);
  const [wordFocusMode, setWordFocusMode] = useState(() => {
    try { return localStorage.getItem('word_focus_mode') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('word_focus_mode', String(wordFocusMode)); } catch { /* Session-only when storage is unavailable. */ }
    if (wordFocusMode) {
      setShowUserMenu(false);
      setShowBlogMenu(false);
    }
  }, [wordFocusMode]);

  const authSource = localStorage.getItem('auth_source');
  const returnUrl = localStorage.getItem('returnUrl');


  const handleLogout = async () => {
    const authSource = localStorage.getItem('auth_source');
    if (authSource === 'wordpress') {
      // Clear storage and redirect in one shot — avoid the React re-render
      // before navigation completes.
      localStorage.removeItem('token');
      localStorage.removeItem('auth_source');
      localStorage.removeItem('expiresAt');
      localStorage.removeItem('returnUrl');
      sessionStorage.removeItem('refreshToken');
      window.location.href = '/auth/session-expired';
      return;
    }
    await logout();
    navigate('/auth/session-expired');
  };

  const isAdmin = user?.role === 'super_admin';
  const wordWorkspace = workspaceMode === 'word';

  useEffect(() => {
    document.body.classList.toggle('app-layout-word-workspace', wordWorkspace);
    if (wordWorkspace) {
      setShowBlogMenu(false);
      setMobileMenuOpen(false);
    }
    return () => document.body.classList.remove('app-layout-word-workspace');
  }, [wordWorkspace]);

  return (
    <div className={`app-layout ${wordWorkspace ? 'app-layout--word' : ''} ${wordWorkspace && wordFocusMode ? 'app-layout--word-focus' : ''}`} data-theme={theme}>
      <header className="app-header">
        <div className="header-left">
          {!wordWorkspace && <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>}
          <div className="logo">
            <img src="/logo.png" alt="CemSites" className="logo-img" />
          </div>
          {authSource === 'wordpress' && returnUrl && (
            <a
              href={returnUrl}
              className="back-to-app-btn"
            >
              <ArrowLeft size={16} />
              <span className="back-to-app-label">Back to CemSites</span>
            </a>
          )}
        </div>

        <div className="header-center">
          {user?.blogs && (user.blogs.length > 1 || wordWorkspace) && (
            <div className="blog-selector">
              <button
                className="blog-selector-btn"
                onClick={() => setShowBlogMenu(!showBlogMenu)}
                disabled={wordWorkspace}
                title={wordWorkspace ? 'Site switching is disabled while a Word document is open' : 'Switch site'}
              >
                {selectedBlog ? `${selectedBlog.blog_id} - ${selectedBlog.domain || selectedBlog.name}` : 'Select Blog'}
                {!wordWorkspace && <ChevronDown size={16} />}
              </button>
              {showBlogMenu && !wordWorkspace && (
                <>
                  <div className="dropdown-backdrop" onClick={() => setShowBlogMenu(false)} />
                  <div className="dropdown-menu">
                    {user.blogs.map(blog => (
                      <button
                        key={blog.id}
                        className={`dropdown-item ${blog.id === selectedBlog?.id ? 'active' : ''}`}
                        onClick={() => {
                          switchBlog(blog);
                          setShowBlogMenu(false);
                        }}
                      >
                        {blog.blog_id} - {blog.domain || blog.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="header-right">
          <button
            className="help-btn"
            title="Guides & Help"
            onClick={() => navigate('/landing')}
          >
            <HelpCircle size={20} />
          </button>
          <div className="user-menu">
            <button
              className="user-menu-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <span className="user-name">{user?.name}</span>
              <ChevronDown size={16} />
            </button>
            {showUserMenu && (
              <>
                <div className="dropdown-backdrop" onClick={() => setShowUserMenu(false)} />
                <div className="dropdown-menu user-dropdown">
                  <div className="dropdown-header">
                    <div className="user-avatar large">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div className="dropdown-user-name">{user?.name}</div>
                      <div className="dropdown-user-email">{user?.email}</div>
                    </div>
                  </div>
                  {!customerDocsOnly && <NavLink
                    to="/settings"
                    className="dropdown-item"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Settings size={16} />
                    Settings
                  </NavLink>}
                  <button className="dropdown-item danger" onClick={handleLogout}>
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div id="blue__bar" aria-hidden="true" />

      <div className="app-body">
        {!wordWorkspace && <nav className={`app-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="sidebar-nav">
            {!customerDocsOnly && (
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <LayoutDashboard size={20} />
                <span>Dashboard</span>
              </NavLink>
            )}
            <NavLink
              to="/customer-documents"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <UserCheck size={20} />
              <span>Customer Docs</span>
            </NavLink>
            {!customerDocsOnly && isAdmin && (
              <>
                <NavLink
                  to="/admin/users"
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Users size={20} />
                  <span>Users</span>
                </NavLink>
                <NavLink
                  to="/admin/blogs"
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Globe size={20} />
                  <span>Blogs</span>
                </NavLink>
              </>
            )}
            {!customerDocsOnly && (
              <NavLink
                to="/settings"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Settings size={20} />
                <span>Settings</span>
              </NavLink>
            )}
          </div>
        </nav>}

        <main className="app-main">
          <Outlet context={{ workspaceMode, setWorkspaceMode, wordFocusMode, setWordFocusMode }} />
        </main>
      </div>

      {!wordWorkspace && <footer className="support_footer">
        <p>
          Need help? Please email us at{' '}
          <a href="mailto:support@cemsites.com">support@cemsites.com</a>
          {' '}or call us at{' '}
          <a href="tel:1-877-783-9626">1-877-783-9626</a>.
        </p>
      </footer>}

    </div>
  );
}

export default AppLayout;
